import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';

// Admin-only page for toggling site-wide maintenance mode. The API enforces the admin requirement
// (@Roles('admin') on PATCH /maintenance) -- this page hiding itself from non-admins is a
// convenience, not the security boundary.
function Maintenance() {
  const { currentUser, maintenance, refresh } = useApp();
  const [message, setMessage] = useState(maintenance?.maintenanceMessage || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isOn = !!maintenance?.maintenanceMode;

  // Defense in depth against a stray route: an admin-only page shouldn't render its controls just
  // because someone typed the URL. (The API would reject the PATCH regardless.)
  if (currentUser && currentUser.role !== 'admin') {
    return (
      <div>
        <div className="page-header">
          <div className="page-eyebrow">Administration</div>
          <h1 className="page-title">Maintenance Mode</h1>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            This page is only available to administrators.
          </div>
        </div>
      </div>
    );
  }

  async function handleToggle(nextEnabled) {
    setSaving(true);
    setError('');
    try {
      await apiFetch('/maintenance', {
        method: 'PATCH',
        body: JSON.stringify({
          maintenanceMode: nextEnabled,
          ...(nextEnabled ? { maintenanceMessage: message.trim() || undefined } : {}),
        }),
      });
      // Refresh shared state so the banner and gate update immediately, without a page reload.
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const updatedByLabel = maintenance?.updatedBy
    ? `${maintenance.updatedBy.name} (${maintenance.updatedBy.email})`
    : null;

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Administration</div>
        <h1 className="page-title">Maintenance Mode</h1>
        <p className="page-subtitle">
          Lock the app for everyone except administrators while you make changes.
        </p>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 20,
          background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)',
          fontSize: 13, color: 'var(--red)',
        }}>⚠ {error}</div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Current status</div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 20, color: isOn ? 'var(--yellow)' : 'var(--green)' }}>
            {isOn ? '●' : '●'}
          </span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-dark)' }}>
              {isOn ? 'Maintenance mode is ON' : 'Maintenance mode is OFF'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-mid)' }}>
              {isOn
                ? 'Everyone except administrators is locked out right now.'
                : 'The app is available to everyone as normal.'}
            </div>
          </div>
        </div>

        {updatedByLabel && (
          <div style={{ fontSize: 12, color: 'var(--text-mid)', marginBottom: 16 }}>
            Last changed by {updatedByLabel}
            {maintenance.updatedAt && ` on ${new Date(maintenance.updatedAt).toLocaleString()}`}
          </div>
        )}

        <div className="form-group" style={{ marginBottom: 16 }}>
          <label className="form-label">Message shown to locked-out users (optional)</label>
          <textarea
            className="form-textarea"
            rows={3}
            maxLength={500}
            placeholder="e.g. Upgrading the database — back by 2pm ET."
            value={message}
            onChange={e => setMessage(e.target.value)}
            disabled={isOn}
          />
          <div style={{ fontSize: 11, color: 'var(--text-mid)', marginTop: 4 }}>
            {isOn
              ? 'Turn maintenance off to change the message.'
              : `${message.length}/500 — shown on the lockout screen.`}
          </div>
        </div>

        {isOn ? (
          <button className="btn btn-success" disabled={saving} onClick={() => handleToggle(false)}>
            {saving ? 'Working…' : 'Turn maintenance mode OFF'}
          </button>
        ) : (
          <button className="btn btn-primary" disabled={saving} onClick={() => handleToggle(true)}>
            {saving ? 'Working…' : 'Turn maintenance mode ON'}
          </button>
        )}
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">What this does</div>
        <ul style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
          <li>Non-admins see a "down for maintenance" screen instead of the app.</li>
          <li>
            The API rejects their requests too — turning this on genuinely stops data changing,
            not just the screens that show it.
          </li>
          <li>Administrators keep full access, with a banner as a reminder it's on.</li>
          <li>Anyone already signed in is affected immediately; no one is signed out.</li>
        </ul>
        <div style={{ marginTop: 16 }}>
          <Link to="/administration" className="btn btn-outline">← Back to Administration</Link>
        </div>
      </div>
    </div>
  );
}

export default Maintenance;
